import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import SearchIcon from "@mui/icons-material/Search";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import {
  activateRecurringSchedule,
  deactivateRecurringSchedule,
  deleteRecurringSchedule,
  getRecurringSchedules,
} from "../../../fetchers/cmmsRecurringSchedule";
import { fetchAssetHierarchyTree } from "../../../fetchers/cmmsAssetHierarchy";
import AxionControls from "../../../components/controls/AxionControls";
import { FormattedMessage, useIntl } from "react-intl";
import cmmsMessages from "../../cmms/cmmsMessages";
import { CmmsEmptyState, CmmsErrorState, formatCmmsError } from "../../cmms/components/CmmsStates";
import { HideIfNoRole } from "../../../auth/authUi";
import { CMMS_MANAGER_ROLES } from "../../cmms/cmmsRoles";
import messages from "../messages";
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import { flattenAssetNodes } from "../../cmms/assetHierarchyUtils";
import {
  cmmsTypeScale,
  getAssetHierarchyHeaderSx,
  getAssetHierarchyPalette,
  getContainedButtonSx,
  getInputLabelSx,
  getMenuSx,
  getPageHeaderIconSx,
  getPageSubtitleSx,
  getPageTitleSx,
  getSelectSx,
  getStatLabelSx,
  getStatValueSx,
  getStatisticTileSx,
} from "../../cmms/assetHierarchyStyles";
import { filterRecurringSchedules } from "../recurringScheduleSearch";
import RecurringSchedulesDataGrid from "./RecurringSchedulesDataGrid";
import RecurringScheduleFormDialog from "./RecurringScheduleFormDialog";
import ScheduleResetDrawer from "./ScheduleResetDrawer";

const ACTIVE_ALL = "ALL";
const ACTIVE_TRUE = "TRUE";
const ACTIVE_FALSE = "FALSE";

export default function RecurringSchedulesPanel() {
  const theme = useTheme();
  const intl = useIntl();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const [companyKey] = useLocalStorage("companyKey", 1);

  const [activeFilter, setActiveFilter] = useState(ACTIVE_ALL);
  const [searchText, setSearchText] = useState("");
  const [formState, setFormState] = useState({ open: false, scheduleId: null });
  const [confirmation, setConfirmation] = useState(null);
  const [resetScheduleId, setResetScheduleId] = useState(null);

  const apiFilters = useMemo(() => {
    const f = {};
    if (activeFilter === ACTIVE_TRUE) f.isActive = true;
    if (activeFilter === ACTIVE_FALSE) f.isActive = false;
    return f;
  }, [activeFilter]);

  const schedulesQuery = useQuery(
    ["cmmsRecurringSchedules", companyKey, apiFilters],
    () => getRecurringSchedules(companyKey, apiFilters),
    { enabled: Boolean(companyKey), staleTime: 30000 },
  );

  const treeQuery = useQuery(
    ["cmmsAssetHierarchyTree", companyKey, false],
    () => fetchAssetHierarchyTree(companyKey, false),
    { enabled: Boolean(companyKey), staleTime: 300000 },
  );

  const assetNodeNameById = useMemo(() => {
    const map = new Map();
    flattenAssetNodes(treeQuery.data).forEach((node) => map.set(node.id, node.name));
    return map;
  }, [treeQuery.data]);

  const deleteMutation = useMutation({ mutationFn: (id) => deleteRecurringSchedule(companyKey, id) });
  const activateMutation = useMutation({ mutationFn: (id) => activateRecurringSchedule(companyKey, id) });
  const deactivateMutation = useMutation({ mutationFn: (id) => deactivateRecurringSchedule(companyKey, id) });

  const schedules = useMemo(() => schedulesQuery.data ?? [], [schedulesQuery.data]);

  // Client-side, and the asset name has to be resolved before it can be matched — hence the map
  // rather than the raw rows. The Active dropdown beside it is answered by the API instead.
  const visibleSchedules = useMemo(
    () => filterRecurringSchedules(schedules, searchText, { assetNodeNameById }),
    [schedules, searchText, assetNodeNameById],
  );

  /*
   * The drawer follows the row, not a copy of it.
   *
   * Holding the id and looking the schedule up again on every render is what keeps the drawer
   * current when the list refetches — after a reset, above all, since the reset moves the very
   * anchor the drawer is describing. Keeping the row object in state would leave it showing the
   * schedule as it was when the drawer opened. Same reason ToolsPanel does it this way.
   */
  const resetSchedule = useMemo(
    () => schedules.find((row) => row.id === resetScheduleId) ?? null,
    [schedules, resetScheduleId],
  );

  const hasActiveFilters = activeFilter !== ACTIVE_ALL || searchText.trim() !== "";

  const handleClearFilters = () => {
    setActiveFilter(ACTIVE_ALL);
    setSearchText("");
  };

  const handleAdd = () => setFormState({ open: true, scheduleId: null });
  const handleEdit = (row) => setFormState({ open: true, scheduleId: row.id });
  const closeForm = () => setFormState({ open: false, scheduleId: null });
  const handleSuccess = async () => {
    closeForm();
    await schedulesQuery.refetch();
  };

  const handleDelete = (row) => {
    setConfirmation({
      title: intl.formatMessage(messages.deleteTitle),
      message: intl.formatMessage(messages.deleteMessage, { title: row.title }),
      confirmText: intl.formatMessage(cmmsMessages.delete),
      onConfirm: async () => {
        try {
          await deleteMutation.mutateAsync(row.id);
          setConfirmation(null);
          pushNotification(intl.formatMessage(messages.toastDeleted));
          await schedulesQuery.refetch();
        } catch (error) {
          pushErrorMessage(formatCmmsError(error, intl.formatMessage(messages.loadFailed)));
        }
      },
    });
  };

  const handleToggleActive = async (row) => {
    try {
      if (row.isActive) {
        await deactivateMutation.mutateAsync(row.id);
        pushNotification(intl.formatMessage(messages.toastDeactivated));
      } else {
        await activateMutation.mutateAsync(row.id);
        pushNotification(intl.formatMessage(messages.toastActivated));
      }
      await schedulesQuery.refetch();
    } catch (error) {
      pushErrorMessage(formatCmmsError(error, intl.formatMessage(messages.loadFailed)));
    }
  };

  // react-query v4 exposes isLoading on mutations; isPending is the v5 name.
  const isMutating = deleteMutation.isPending || deleteMutation.isLoading
    || activateMutation.isPending || activateMutation.isLoading
    || deactivateMutation.isPending || deactivateMutation.isLoading;
  // Counts what is on screen, search included — the same call the work order page's Total tile
  // makes. A number above a grid that disagrees with it is worse than no number.
  const activeCount = visibleSchedules.filter((s) => s.isActive).length;

  return (
    <Box>
      <Box sx={getAssetHierarchyHeaderSx(cmmsColors)}>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <EventRepeatIcon sx={getPageHeaderIconSx(cmmsColors)} />
            <Box>
              <Typography variant={cmmsTypeScale.pageTitle} sx={getPageTitleSx(cmmsColors)}>
                <FormattedMessage {...messages.pageTitle} />
              </Typography>
              <Typography variant={cmmsTypeScale.pageSubtitle} sx={getPageSubtitleSx(cmmsColors)}>
                <FormattedMessage {...messages.pageSubtitle} />
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Box sx={getStatisticTileSx(cmmsColors)}>
              <Typography variant={cmmsTypeScale.statLabel} sx={getStatLabelSx(cmmsColors)}>
                <FormattedMessage {...messages.statActive} />
              </Typography>
              <Typography variant={cmmsTypeScale.statValue} sx={getStatValueSx(cmmsColors)}>
                {activeCount} / {visibleSchedules.length}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ mt: 2, pt: 2, borderTop: `1px solid ${cmmsColors.border}` }}>
          {/*
            * Creating a schedule is Cmms_Manager. The route admits [1,2,3,4] because reading the
            * list is Cmms_Field, so the gate belongs on the button — the same split the spare
            * parts page uses, and the same one the empty state below already had.
            *
            * The module's own button styling, not MUI's default. palette.primary.main is
            * colors.primary[500] and so is the header surface behind it — the same hex — so a
            * bare contained button is invisible until hovered.
            */}
          <HideIfNoRole anyOf={CMMS_MANAGER_ROLES}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              sx={getContainedButtonSx(cmmsColors)}
            >
              <FormattedMessage {...messages.addSchedule} />
            </Button>
          </HideIfNoRole>

          {/*
            * The same search box the register and the work order list carry, in place of the
            * asset dropdown that used to sit here. That dropdown could answer one question, in a
            * control twice the height of the plain MUI selects beside it; the box answers the
            * same question — the asset name is searched — along with the title, the description
            * and the tool.
            *
            * Controlled, so Clear filters can empty it: otherwise the button leaves the list
            * looking filtered with the reason still in the field. Wide enough for its own label,
            * because MUI ellipsis-clips an outlined label that outgrows the notch.
            */}
          <TextField
            size="small"
            sx={{ width: { xs: "100%", sm: 420 } }}
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            label={intl.formatMessage(messages.filterSearch)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="sched-active-filter" sx={getInputLabelSx(cmmsColors)}>{intl.formatMessage(messages.filterActive)}</InputLabel>
            <Select
              labelId="sched-active-filter"
              label={intl.formatMessage(messages.filterActive)}
              value={activeFilter}
              onChange={(event) => setActiveFilter(event.target.value)}
              sx={getSelectSx(cmmsColors)}
              MenuProps={{ PaperProps: { sx: getMenuSx(cmmsColors) } }}
            >
              <MenuItem value={ACTIVE_ALL}><FormattedMessage {...messages.filterAll} /></MenuItem>
              <MenuItem value={ACTIVE_TRUE}><FormattedMessage {...messages.filterActiveOnly} /></MenuItem>
              <MenuItem value={ACTIVE_FALSE}><FormattedMessage {...messages.filterInactiveOnly} /></MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Box>

      <Box sx={{ mt: 2 }}>
        {schedulesQuery.error &&
          <CmmsErrorState
            error={schedulesQuery.error}
            fallback={intl.formatMessage(messages.loadFailed)}
            onRetry={() => schedulesQuery.refetch()}
            isRetrying={schedulesQuery.isFetching}
          />
        }
        {!schedulesQuery.error &&
          <RecurringSchedulesDataGrid
            schedules={visibleSchedules}
            // First load only — see the note in cmmsWorkOrders/index.jsx.
            isLoading={schedulesQuery.isLoading}
            assetNodeNameById={assetNodeNameById}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onToggleActive={handleToggleActive}
            onOpenReset={(row) => setResetScheduleId(row.id)}
            emptyState={
              hasActiveFilters
                ? <CmmsEmptyState
                    icon={<FilterAltOffIcon />}
                    title={intl.formatMessage(messages.emptyFilteredTitle)}
                    hint={intl.formatMessage(messages.emptyFilteredHint)}
                    action={
                      <Button size="small" variant="outlined" color="secondary" onClick={handleClearFilters}>
                        <FormattedMessage {...messages.filterClear} />
                      </Button>
                    }
                  />
                : <CmmsEmptyState
                    icon={<EventRepeatIcon />}
                    title={intl.formatMessage(messages.emptyTitle)}
                    hint={intl.formatMessage(messages.emptyHint)}
                    action={
                      <HideIfNoRole anyOf={CMMS_MANAGER_ROLES}>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={handleAdd}
                          sx={getContainedButtonSx(cmmsColors)}
                        >
                          <FormattedMessage {...messages.addSchedule} />
                        </Button>
                      </HideIfNoRole>
                    }
                  />
            }
          />
        }
      </Box>

      <RecurringScheduleFormDialog
        open={formState.open}
        scheduleId={formState.scheduleId}
        companyKey={companyKey}
        onClose={closeForm}
        onSuccess={handleSuccess}
      />

      <ScheduleResetDrawer
        schedule={resetSchedule}
        companyKey={companyKey}
        onClose={() => setResetScheduleId(null)}
      />

      <AxionControls.AxionConfirmation
        open={Boolean(confirmation)}
        title={confirmation?.title}
        message={confirmation?.message}
        confirmText={confirmation?.confirmText ?? intl.formatMessage(cmmsMessages.confirm)}
        cancelText={intl.formatMessage(cmmsMessages.cancel)}
        onConfirm={confirmation?.onConfirm}
        onCancel={() => setConfirmation(null)}
      />
      <AxionControls.AxionBackdrop open={isMutating} />
    </Box>
  );
}
